import { useState } from 'react';
import { useNotify, useRecordContext } from 'react-admin';
import { Link } from 'react-router-dom';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import Bolt from '@mui/icons-material/Bolt';
import Cancel from '@mui/icons-material/Cancel';
import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  IconButton,
  TextField,
  Typography,
} from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import PropTypes from 'prop-types';

import { claimMGAStatuses, getClaimStatusColor } from '@pumpkincare/claims';

import ClaimPumpkinNowDecision from '../../../../sections/claims/claim-pumpkin-now-decision';
import { dataProvider } from '../../../dataProvider/provider';
import { claimHeaderStyle } from './claim-header-style';

function ClaimHeader({
  opsData = [],
  isAssignOpsEditMode = false,
  onEditModeToggle = () => {},
  onOpsChange = () => {},
  disableComponents = false,
  isPumpkinNowLoading = false,
  pumpkinNowRequest = null,
}) {
  const record = useRecordContext();
  const notify = useNotify();
  const classes = claimHeaderStyle();
  const isPumpkinNowEligible = record?.is_pumpkin_now_eligible;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [pumpkinNowTempStatus, setPumpkinNowTempStatus] = useState('');
  const pumpkinNowStatus =
    pumpkinNowTempStatus || pumpkinNowRequest?.pumpkin_now_status || '';

  const { mutateAsync: updatePumpkinNow, isPending } = useMutation({
    mutationFn: data =>
      dataProvider(
        'PATCH',
        `claims/${record?.id}/pumpkin-now/${pumpkinNowRequest?.id}`,
        { data }
      ),
  });

  function handleDeferButtonClick() {
    setDialogOpen(true);
  }

  function onDialogClose() {
    setDialogOpen(false);
  }

  function submitPumpkinNowDecision(action, deferralReason, notes) {
    const payload = { pumpkin_now_status: action };
    if (deferralReason) payload.deferral_reason = deferralReason;
    if (notes) payload.deferral_notes = notes;

    updatePumpkinNow(payload)
      .then(() => {
        setPumpkinNowTempStatus(action);
        setDialogOpen(false);
      })
      .catch(error => {
        notify(
          `There was an error while submitting pumpkin now decision. ${error.message}`,
          {
            type: 'error',
          }
        );
      });
  }

  return (
    <>
      <Dialog open={dialogOpen} onClose={onDialogClose} className={classes.dialog}>
        <ClaimPumpkinNowDecision
          onSubmit={submitPumpkinNowDecision}
          isSubmitting={isPending}
          pumpkinNowStatus={pumpkinNowStatus}
          isEstimate={record?.is_estimate}
          isPaymentAtVet={record?.is_for_payment_at_vet}
          modal={true}
        />
      </Dialog>
      <div className={classes.headerWrapper}>
        <Link
          to={`/customers/${record.customer_id}/show/claims`}
          className={classes.link}
        >
          <ArrowBackIosIcon className={classes.arrowBackIcon} />
          <Typography className={classes.claimNumber}>{record.id}</Typography>
        </Link>
        <Typography
          className={classes.status}
          style={{
            backgroundColor: getClaimStatusColor(record.status),
          }}
        >
          {claimMGAStatuses[record.status]}
        </Typography>
        <div className={classes.headerWrapper}>
          {isAssignOpsEditMode ? (
            <Autocomplete
              options={opsData}
              data-testid={'ops-autocomplete'}
              getOptionLabel={option => option.name}
              style={{ width: '250px', marginBottom: '8px' }}
              onChange={(event, values) => onOpsChange(values, record.id)}
              renderOption={(props, option) => (
                <div
                  {...props}
                  key={option.email}
                  style={{ paddingLeft: '8px', display: 'block' }}
                >
                  <div className={classes.opsName}>{option.name}</div>
                  <div className={classes.opsEmail}>{option.email}</div>
                </div>
              )}
              renderInput={params => <TextField {...params} variant='outlined' />}
            />
          ) : (
            <Button onClick={() => onEditModeToggle()} disabled={disableComponents}>
              <AccountCircleIcon />
              <Typography className={classes.owner}>
                {record.current_assignee_name || 'Unassigned'}
              </Typography>
            </Button>
          )}
        </div>
        {isPumpkinNowEligible ? (
          <div className={classes.pumpkinNowWrapper}>
            {!isPumpkinNowLoading ? (
              <>
                <Bolt className={classes.bolt} />
                <div className={classes.pumpkinNowText}>
                  Pumpkin Now {pumpkinNowStatus}
                </div>
                {pumpkinNowStatus === 'EXPEDITED' ? (
                  <IconButton
                    onClick={handleDeferButtonClick}
                    size='small'
                    color='error'
                    aria-label={'Defer'}
                  >
                    <Cancel />
                  </IconButton>
                ) : null}
              </>
            ) : (
              <CircularProgress size={20} />
            )}
          </div>
        ) : null}
      </div>
    </>
  );
}

ClaimHeader.propTypes = {
  opsData: PropTypes.array,
  isAssignOpsEditMode: PropTypes.bool,
  onEditModeToggle: PropTypes.func,
  onOpsChange: PropTypes.func,
  disableComponents: PropTypes.bool,
  pumpkinNowRequest: PropTypes.shape({
    id: PropTypes.string,
    pumpkin_now_status: PropTypes.string,
  }),
  isPumpkinNowLoading: PropTypes.bool,
};

export default ClaimHeader;
